import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiMail, FiPlus, FiSend, FiClock, FiLock } from 'react-icons/fi';
import { API_URL } from '../config';

/**
 * Lists letters that reference a given quote/job/invoice. Embedded into the
 * detail page for that record. Provides a "New Letter" shortcut that opens the
 * composer pre-linked to this record.
 */
function RelatedLetters({ relatedType, relatedId }) {
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!relatedType || !relatedId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(
        `${API_URL}/letters?related_type=${relatedType}&related_id=${relatedId}`,
        config
      );
      setLetters(res.data || []);
    } catch (err) {
      console.error('Failed to load related letters:', err);
    } finally {
      setLoading(false);
    }
  }, [relatedType, relatedId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{
      marginTop: '2rem',
      padding: '1.5rem',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#2563eb', fontSize: '18px' }}>
          <FiMail /> Letters
        </h2>
        <button
          onClick={() => navigate(`/letters/new?related_type=${relatedType}&related_id=${relatedId}`)}
          style={{
            padding: '8px 14px', backgroundColor: '#2563eb', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer',
            fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FiPlus size={14} /> New Letter
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '12px', color: '#9ca3af', fontSize: '13px' }}>Loading...</div>
      ) : letters.length === 0 ? (
        <div style={{ padding: '12px 0', color: '#9ca3af', fontSize: '13px' }}>
          No letters yet for this record.
        </div>
      ) : (
        <div>
          {letters.map(letter => (
            <div
              key={letter.id}
              onClick={() => navigate(`/letters/${letter.id}`)}
              style={{
                padding: '10px 12px', marginBottom: '6px', borderRadius: '6px',
                border: '1px solid #e5e7eb', cursor: 'pointer', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', gap: '10px',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#111827', fontWeight: 500 }}>
                  {letter.visibility === 'admin_only' && (
                    <FiLock size={11} style={{ color: '#dc2626' }} title="Admin only" />
                  )}
                  {letter.subject || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>(no subject)</span>}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  To {letter.recipient_name || letter.recipient_company || letter.recipient_email || '—'}
                  {letter.created_by_name && <> · by {letter.created_by_name}</>}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {letter.status === 'sent' ? (
                  <>
                    <FiSend size={11} />
                    Sent {letter.sent_at && new Date(letter.sent_at + 'Z').toLocaleDateString()}
                  </>
                ) : (
                  <>
                    <FiClock size={11} /> Draft
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RelatedLetters;
